import { computed, ref, type Ref } from "vue"
import { Folder, Hash, KanbanSquare, Volume2 } from "lucide-vue-next"
import { toast } from "vue-sonner"
import { useNotificationSettingsStore } from "~/stores/notificationSettings"
import { useGroupsStore } from "~/stores/groups"
import { ChannelType } from "~/types/groups"
import {
  MuteDuration,
  NotificationLevel,
  NotificationTargetType,
  type NotificationTargetTypeValue,
  type ResolvedNotification,
} from "~/lib/notifications/notificationPolicy"

export interface NotificationTargetInfo {
  id: string
  type: NotificationTargetTypeValue
  name: string
  categoryId?: string
  channelType?: ChannelType
}

// The «Каналы и категории» part of a group's notification settings — every category/channel with a
// setting of its own, plus the ones just picked through «Добавить…» (shown with «По умолчанию» until
// they get one). Shared by the desktop Dialog and the mobile Drawer (NotificationSettingsDialog.vue,
// NotificationSettingsMobileDrawer.vue).
export function useNotificationOverrides(groupId: Ref<string>) {
  const store = useNotificationSettingsStore()
  const groupsStore = useGroupsStore()

  const added = ref<string[]>([])

  const targets = computed(() => {
    const map = new Map<string, NotificationTargetInfo>()
    for (const category of groupsStore.groupDetails[groupId.value]?.categories ?? []) {
      map.set(category.id.toLowerCase(), { id: category.id, type: NotificationTargetType.Category, name: category.name })
      for (const channel of category.channels) {
        if (channel.type === ChannelType.DirectMessage || channel.type === ChannelType.GroupMessage) continue
        map.set(channel.id.toLowerCase(), {
          id: channel.id,
          type: NotificationTargetType.Channel,
          name: channel.name,
          categoryId: category.id,
          channelType: channel.type,
        })
      }
    }
    return map
  })

  function resolve(info: NotificationTargetInfo): ResolvedNotification {
    return info.type === NotificationTargetType.Category
      ? store.resolveCategory(groupId.value, info.id)
      : store.resolveChannel(groupId.value, info.id, info.categoryId)
  }

  const overrideRows = computed(() => {
    const ids = new Set<string>()
    const rows: { info: NotificationTargetInfo, resolved: ResolvedNotification }[] = []
    const push = (id: string) => {
      const key = id.toLowerCase()
      const info = targets.value.get(key)
      if (!info || ids.has(key)) return
      ids.add(key)
      rows.push({ info, resolved: resolve(info) })
    }
    for (const o of store.settingsFor(groupId.value)?.overrides ?? []) push(o.target_id)
    for (const id of added.value) push(id)
    return rows
  })

  const addableCategories = computed(() => {
    const taken = new Set(overrideRows.value.map((r) => r.info.id.toLowerCase()))
    return (groupsStore.groupDetails[groupId.value]?.categories ?? [])
      .map((category) => ({
        category,
        channels: category.channels.filter((c) =>
          c.type !== ChannelType.DirectMessage && c.type !== ChannelType.GroupMessage && !taken.has(c.id.toLowerCase())),
        categoryTaken: taken.has(category.id.toLowerCase()),
      }))
      .filter((entry) => !entry.categoryTaken || entry.channels.length > 0)
  })

  function addTarget(id: string) {
    if (id && !added.value.includes(id)) added.value = [...added.value, id]
  }

  function targetInfo(id: string): NotificationTargetInfo | undefined {
    return targets.value.get(id.toLowerCase())
  }

  function targetIcon(info: NotificationTargetInfo) {
    if (info.type === NotificationTargetType.Category) return Folder
    if (info.channelType === ChannelType.Voice) return Volume2
    if (info.channelType === ChannelType.Board) return KanbanSquare
    return Hash
  }

  async function removeRow(info: NotificationTargetInfo, resolved: ResolvedNotification) {
    added.value = added.value.filter((id) => id.toLowerCase() !== info.id.toLowerCase())
    if (resolved.ownLevel === NotificationLevel.Inherit && !resolved.ownMutedUntil) return
    try {
      await store.update(groupId.value, info.type, info.id, {
        level: NotificationLevel.Inherit,
        ...(resolved.ownMutedUntil ? { mute: MuteDuration.Unmute } : {}),
      })
    } catch {
      toast.error("Не удалось изменить настройки уведомлений")
    }
  }

  function resetAdded() {
    added.value = []
  }

  return { overrideRows, addableCategories, addTarget, targetInfo, targetIcon, removeRow, resetAdded, resolve }
}
